export type PixelVendor =
    | 'gtm'
    | 'meta'
    | 'googleGtag'
    | 'axon'
    | 'blotout'
    | 'attentive'
    | 'postscript'
    | 'tripleWhale'
    | 'klaviyo'
    | 'elevar'
    | 'converge'

export const UNREADY_VENDOR_TIMEOUT_MS = 45_000

const queues = new Map<PixelVendor, Array<() => void>>()
const readyVendors = new Set<PixelVendor>()
const readyTimeouts = new Map<PixelVendor, ReturnType<typeof setTimeout>>()
let configuredVendors: Set<PixelVendor> | null = null

function getQueue(vendor: PixelVendor): Array<() => void> {
    if (!queues.has(vendor)) queues.set(vendor, [])
    return queues.get(vendor)!
}

function clearUnreadyTimeout(vendor: PixelVendor): void {
    const timer = readyTimeouts.get(vendor)
    if (timer === undefined) return
    clearTimeout(timer)
    readyTimeouts.delete(vendor)
}

function scheduleUnreadyTimeout(vendor: PixelVendor): void {
    if (readyVendors.has(vendor) || readyTimeouts.has(vendor)) return
    readyTimeouts.set(
        vendor,
        setTimeout(() => {
            readyTimeouts.delete(vendor)
            markVendorReady(vendor)
        }, UNREADY_VENDOR_TIMEOUT_MS),
    )
}

export function enqueue(vendor: PixelVendor, fn: () => void): void {
    if (readyVendors.has(vendor)) {
        fn()
        return
    }
    getQueue(vendor).push(fn)
    scheduleUnreadyTimeout(vendor)
}

export function runWhenReady(vendor: PixelVendor, fn: () => void): void {
    if (configuredVendors && !configuredVendors.has(vendor)) {
        fn()
        return
    }
    enqueue(vendor, fn)
}

export function markVendorReady(vendor: PixelVendor): void {
    clearUnreadyTimeout(vendor)
    readyVendors.add(vendor)
    const pending = getQueue(vendor)
    while (pending.length) pending.shift()?.()
}

export function setConfiguredPixelVendors(vendors: PixelVendor[]): void {
    configuredVendors = new Set(vendors)
    for (const vendor of [...queues.keys()]) {
        if (!configuredVendors.has(vendor)) {
            markVendorReady(vendor)
        }
    }
}

export function resetPixelRuntime(): void {
    for (const timer of readyTimeouts.values()) clearTimeout(timer)
    readyTimeouts.clear()
    queues.clear()
    readyVendors.clear()
    configuredVendors = null
}

export function pushGtmEvent(payload: Record<string, unknown>): void {
    runWhenReady('gtm', () => {
        window.dataLayer?.push(payload)
    })
}

// Bridge for inline pixel snippets to mark themselves ready from inside the
// script body. This is required because Next.js `<Script onReady>` fires
// before an inline body executes, so wrapping components can't rely on
// `onReady` for the readiness signal — the vendor global they queue against
// (fbq, gtag, axon, etc.) would not exist yet.
if (typeof window !== 'undefined') {
    window.__sfMarkVendorReady = (vendor) =>
        markVendorReady(vendor as PixelVendor)
}
